import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DateRangeControl from '../../AnalyticsPro/DateRangeControl';
import Popover from './Popover';
import { useDashboardFilters } from './useDashboardFilters';

const PRESET_LABELS = {
    today: 'Today',
    yesterday: 'Yesterday',
    last7: 'Last 7 days',
    last30: 'Last 30 days',
    mtd: 'Month to date',
    qtd: 'Quarter to date',
    ytd: 'Year to date',
};

export default function DateRangeChip() {
    const { t } = useTranslation();
    const { filters, setDateRange } = useDashboardFilters();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const label = useMemo(() => formatLabel(filters.dateRange, t), [filters.dateRange, t]);

    return (
        <div className="v3-df-chip-wrap">
            <button
                ref={anchorRef}
                type="button"
                className={`v3-df-chip${open ? ' is-open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <span className="v3-df-chip-icon" aria-hidden>📅</span>
                <span className="v3-df-chip-label">{label}</span>
                <span className="v3-df-chip-caret" aria-hidden>▾</span>
            </button>
            <Popover open={open} onClose={() => setOpen(false)} anchorRef={anchorRef} align="left">
                <div className="v3-df-popover-title">{t('Date range')}</div>
                <DateRangeControl
                    range={filters.dateRange}
                    onChange={(r) => setDateRange(r)}
                />
            </Popover>
        </div>
    );
}

function formatLabel(range, t) {
    if (!range || !range.from || !range.to) return t('Select date range');
    // If the range matches a known preset, use its friendly name.
    const preset = detectPreset(range);
    if (preset) return t(PRESET_LABELS[preset]);
    if (range.from === range.to) return formatDay(range.from);
    return `${formatDay(range.from)} – ${formatDay(range.to)}`;
}

function formatDay(iso) {
    if (!iso) return '';
    const d = new Date(`${iso}T00:00:00`);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Local copy so we don't import from the Analytics control's internals.
// Keep in sync with the PRESETS list in DateRangeControl.
function detectPreset(range) {
    // We import computePreset lazily to avoid circular deps in tests.
    // eslint-disable-next-line global-require
    const { computePreset } = require('../../AnalyticsPro/DateRangeControl');
    const keys = ['today', 'yesterday', 'last7', 'last30', 'mtd', 'qtd', 'ytd'];
    for (const k of keys) {
        const r = computePreset(k);
        if (r && r.from === range.from && r.to === range.to) return k;
    }
    return null;
}
